// ── Adapter Supabase do ICsRepository ─────────────────────────────
// Schema `cs` (supabase/migrations/20260826120000_cs_schema.sql) +
// bucket privado `cs-planilhas`. É a única camada que conhece PostgREST.

import { getSupabase } from '@infra/supabase/client';
import type {
  ArquivoPlanilha,
  AtualizarVidaInput,
  ClienteHub,
  ClienteImplantacao,
  ClienteImplantacaoResultado,
  Contrato,
  ContaHubDaEmpresa,
  ContratoDados,
  ContratoDaCarteira,
  ContratoDocumento,
  ContratoModalidade,
  Competencia,
  PreviaBoleto,
  CompetenciaStatus,
  ContratoStatus,
  CriarImportacaoInput,
  CriarVidaInput,
  DocumentoStatus,
  DocumentoTipo,
  EmpresaParaRota,
  EmpresaParaVinculo,
  EnvioBloqueado,
  EnvioBloqueadoRegistro,
  EstadoFicha,
  FichaClienteRow,
  GatilhoEmpresa,
  GrupoHub,
  ICsRepository,
  IdentificadoresDaEmpresa,
  Importacao,
  ImportacaoFormato,
  ImportacaoStatus,
  GatilhoStatus,
  GatilhoTipo,
  GatilhoTransporte,
  ModeloHub,
  NegociacaoPlanilha,
  PlanilhaErro,
  PlanoContratado,
  RegistrarGatilhoInput,
  RepresentanteHub,
  RotaIntegracao,
  RotaVia,
  SugestaoVinculoHub,
  UltimaSincronizacaoHub,
  VidaImportada,
  UpsertContratoInput,
  Vida,
  VidaMaisCobranca,
  VidaStatus,
  VidaTipo,
} from '@modules/cs';
import { contratoStatusInicial, ehDependente, emptyContratoDados, estadoDaFicha, hubDaResposta, onlyDigits, qualificacaoOuPadrao } from '@modules/cs';
import type {
  ImportacaoHistorico, LinhaParaRascunho, PlanilhaLinha, PreparoBoleto, VidaRascunho, VidaRascunhoStatus,
} from '@modules/cs';

export const CS_BUCKET = 'cs-planilhas';

const ROTA_PADRAO: RotaIntegracao = { contratoVia: 'n8n', cobrancaVia: 'n8n' };

/**
 * O schema `cs` não existe no `Database` gerado até alguém rodar
 * `supabase gen types --schema public --schema cs`, e `.schema('cs')` não
 * tipa antes disso.
 *
 * O cast fica isolado NESTE ponto — e some sozinho quando os tipos forem
 * regerados, sem tocar em nenhum dos métodos abaixo.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CsSchema = any;

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase não inicializado.');
  return s;
};

const cs = (): CsSchema =>
  (client() as unknown as { schema(nome: string): CsSchema }).schema('cs');

const CONTRATO_COLUMNS = `
  id, workspace_id, empresa_id, sale_id, lead_id,
  razao_social, cnpj, endereco, email,
  representante_nome, representante_cpf, email_assinatura,
  representante_qualificacao, representante_declara_poderes,
  modalidade, vida_mais_cobranca,
  vidas_contratadas, valor_mensal_cents, data_venda, dia_vencimento,
  status, created_at
`;

const IMPORTACAO_COLUMNS = `
  id, workspace_id, empresa_id, contrato_id,
  arquivo_path, arquivo_nome, arquivo_hash, formato,
  total_linhas, total_validas, total_erros, total_descartadas, total_rascunhos,
  erros, negociacao, status, created_at
`;

const RASCUNHO_COLUMNS = `
  id, workspace_id, empresa_id, importacao_id,
  linha, erros, status, vida_id, created_at, updated_at, resolvido_em
`;

const VIDA_COLUMNS = `
  id, empresa_id, contrato_id, importacao_id, tipo, titular_id,
  nome, cpf, data_nascimento, email, telefone,
  plano_id, plano_nome_raw, odonto, psicologo, nome_mae,
  status, seat_public_id, ativo
`;

// ── Mapeamento ────────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */
const mapContrato = (row: any): Contrato => ({
  id: row.id,
  workspaceId: row.workspace_id,
  empresaId: row.empresa_id,
  saleId: row.sale_id ?? null,
  leadId: row.lead_id ?? null,
  dados: {
    razaoSocial:       row.razao_social ?? '',
    cnpj:              row.cnpj ?? '',
    endereco:          row.endereco ?? '',
    email:             row.email ?? '',
    representanteNome: row.representante_nome ?? '',
    representanteCpf:  row.representante_cpf ?? '',
    emailAssinatura:   row.email_assinatura ?? '',
    representanteQualificacao:   qualificacaoOuPadrao(row.representante_qualificacao),
    representanteDeclaraPoderes: row.representante_declara_poderes === true,
  },
  modalidade: (row.modalidade || 'desconto_folha') as ContratoModalidade,
  vidaMaisCobranca: (row.vida_mais_cobranca || 'desconto_folha') as VidaMaisCobranca,
  vidasContratadas: Number(row.vidas_contratadas || 0),
  valorMensalCents: Number(row.valor_mensal_cents || 0),
  dataVenda: row.data_venda ?? null,
  diaVencimento: Number(row.dia_vencimento || 10),
  status: (row.status || 'pendente') as ContratoStatus,
  createdAt: row.created_at,
});

const mapDocumento = (row: any): ContratoDocumento => ({
  id: row.id,
  contratoId: row.contrato_id,
  tipo: row.tipo as DocumentoTipo,
  provider: row.provider || 'd4sign',
  providerDocId: row.provider_doc_id ?? null,
  status: (row.status || 'pendente') as DocumentoStatus,
  url: row.url ?? null,
  signedAt: row.signed_at ?? null,
});

const mapCompetencia = (row: any): Competencia => ({
  id: row.id,
  contratoId: row.contrato_id,
  competencia: row.competencia,
  vidas: Number(row.vidas || 0),
  valorCents: Number(row.valor_cents || 0),
  vencimento: row.vencimento ?? null,
  status: row.status as CompetenciaStatus,
  boletoUrl: row.boleto_url ?? null,
  boletoLinhaDigitavel: row.boleto_linha_digitavel ?? null,
  pagoEm: row.pago_em ?? null,
  pagoManual: row.pago_manual === true,
});

const mapVida = (row: any): Vida => ({
  id: row.id,
  empresaId: row.empresa_id,
  contratoId: row.contrato_id ?? null,
  importacaoId: row.importacao_id ?? null,
  tipo: (row.tipo || 'colaborador') as VidaTipo,
  titularId: row.titular_id ?? null,
  nome: row.nome,
  cpf: row.cpf,
  dataNascimento: row.data_nascimento,
  email: row.email,
  telefone: row.telefone ?? null,
  planoId: row.plano_id ?? null,
  planoNomeRaw: row.plano_nome_raw ?? '',
  odonto: row.odonto === true,
  psicologo: row.psicologo === true,
  nomeMae: row.nome_mae ?? null,
  status: (row.status || 'pendente') as VidaStatus,
  seatPublicId: row.seat_public_id ?? null,
  ativo: row.ativo !== false,
});

const mapImportacao = (row: any): Importacao => ({
  id: row.id,
  workspaceId: row.workspace_id,
  empresaId: row.empresa_id,
  contratoId: row.contrato_id ?? null,
  arquivoPath: row.arquivo_path ?? null,
  arquivoNome: row.arquivo_nome ?? null,
  arquivoHash: row.arquivo_hash ?? null,
  formato: (row.formato ?? null) as ImportacaoFormato | null,
  totalLinhas: Number(row.total_linhas || 0),
  totalValidas: Number(row.total_validas || 0),
  totalErros: Number(row.total_erros || 0),
  erros: Array.isArray(row.erros) ? (row.erros as PlanilhaErro[]) : [],
  negociacao: row.negociacao ?? null,
  status: (row.status || 'processando') as ImportacaoStatus,
  createdAt: row.created_at,
  totalDescartadas: Number(row.total_descartadas || 0),
  totalRascunhos: Number(row.total_rascunhos || 0),
});

const mapImportacaoHistorico = (row: any): ImportacaoHistorico => ({
  ...mapImportacao(row),
  criadoPorNome: row.criado_por_nome ?? null,
  rascunhosPendentes: Number(row.rascunhos_pendentes || 0),
  rascunhosResolvidos: Number(row.rascunhos_resolvidos || 0),
  rascunhosDescartados: Number(row.rascunhos_descartados || 0),
});

// `linha` e `erros` são jsonb gravados em camelCase, como o front os monta —
// voltam como estão. Descartado traz só aba, número e tipo na linha.
const mapRascunho = (row: any): VidaRascunho => {
  const linha = (row.linha && typeof row.linha === 'object' ? row.linha : {}) as Partial<PlanilhaLinha>;
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    empresaId: row.empresa_id,
    importacaoId: row.importacao_id ?? null,
    linha: {
      ...linha,
      numero: Number(linha.numero || 0),
      tipo: (linha.tipo || 'colaborador') as VidaTipo,
      nome: linha.nome ?? '',
      cpf: linha.cpf ?? '',
      dataNascimento: linha.dataNascimento ?? '',
      email: linha.email ?? '',
      telefone: linha.telefone ?? '',
      plano: linha.plano ?? '',
    },
    erros: Array.isArray(row.erros) ? (row.erros as PlanilhaErro[]) : [],
    status: (row.status || 'pendente') as VidaRascunhoStatus,
    vidaId: row.vida_id ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    resolvidoEm: row.resolvido_em ?? null,
  };
};

// As colunas comuns à lista e ao detalhe da fila de implantação. O detalhe
// é a mesma linha com mais campos, então reaproveita este mapa.
const mapClienteImplantacao = (row: any): ClienteImplantacao => ({
  empresaId: row.empresa_id,
  nome: row.nome ?? '',
  cnpj: row.cnpj ?? null,
  contratoStatus: (row.contrato_status ?? null) as ContratoStatus | null,
  modalidade: (row.modalidade ?? null) as ContratoModalidade | null,
  dataVenda: row.data_venda ?? null,
  vidasContratadas: Number(row.vidas_contratadas || 0),
  vidasCadastradas: Number(row.vidas_cadastradas || 0),
  valorMensalCents: Number(row.valor_mensal_cents || 0),
  ultimaImportacaoStatus: (row.ultima_importacao_status ?? null) as ImportacaoStatus | null,
  ultimaImportacaoEm: row.ultima_importacao_em ?? null,
});

// Clientes do Hub (cs.listar_clientes_hub) — espelho do BlueHub, com ou sem
// vínculo confirmado no CRM. `representante`, `grupo` e `sugestoes` chegam
// como jsonb já decodificado pelo PostgREST.
const mapRepresentanteHub = (raw: any): RepresentanteHub | null => (raw ? {
  publicId: raw.public_id,
  nome: raw.nome ?? '',
  email: raw.email ?? '',
  papel: raw.papel ?? '',
} : null);

const mapGrupoHub = (raw: any): GrupoHub | null => (raw ? {
  publicId: raw.public_id,
  nome: raw.nome ?? '',
  papel: raw.papel ?? '',
  principalPublicId: raw.principal_public_id ?? null,
} : null);

const mapSugestaoVinculoHub = (raw: any): SugestaoVinculoHub => ({
  empresaId: raw.empresa_id,
  nome: raw.nome ?? '',
  cnpj: raw.cnpj ?? null,
  workspaceId: raw.workspace_id ?? null,
  como: raw.como === 'filial' ? 'filial' : 'matriz',
});

const mapEmpresaParaVinculo = (raw: any): EmpresaParaVinculo => ({
  empresaId: raw.empresa_id,
  nome: raw.nome ?? '',
  cnpj: raw.cnpj ?? null,
  workspaceId: raw.workspace_id ?? null,
  temContaMatriz: raw.tem_conta_matriz === true,
  filiais: Number(raw.filiais || 0),
});

const mapContaHubDaEmpresa = (raw: any): ContaHubDaEmpresa => ({
  hubPublicId: raw.hub_public_id,
  marca: raw.marca ?? '',
  empresaCnpjId: raw.empresa_cnpj_id ?? null,
  papelCrm: (raw.papel_crm ?? null) as ContaHubDaEmpresa['papelCrm'],
  grupoPublicId: raw.grupo_public_id ?? null,
  papelHub: (raw.papel_hub ?? null) as ContaHubDaEmpresa['papelHub'],
  razaoSocial: raw.razao_social ?? '',
  cnpj: raw.cnpj ?? null,
  status: raw.status ?? '',
  modelo: (raw.modelo ?? null) as ModeloHub | null,
  vidasAtivas: Number(raw.vidas_ativas || 0),
  vidasVendidas: Number(raw.vidas_vendidas || 0),
  representante: mapRepresentanteHub(raw.representante),
  sincronizadoEm: raw.sincronizado_em ?? null,
  ausenteDesde: raw.ausente_desde ?? null,
});

const mapClienteHub = (row: any): ClienteHub => ({
  hubPublicId: row.hub_public_id,
  marca: row.marca ?? '',
  razaoSocial: row.razao_social ?? '',
  cnpj: row.cnpj ?? null,
  status: row.status ?? '',
  modelo: (row.modelo ?? null) as ModeloHub | null,
  vidasAtivas: Number(row.vidas_ativas || 0),
  vidasVendidas: Number(row.vidas_vendidas || 0),
  representante: mapRepresentanteHub(row.representante),
  dataAtivacao: row.data_ativacao ?? null,
  grupo: mapGrupoHub(row.grupo),
  sincronizadoEm: row.sincronizado_em ?? null,
  ausenteDesde: row.ausente_desde ?? null,
  empresaId: row.empresa_id ?? null,
  empresaNome: row.empresa_nome ?? null,
  empresaCnpjId: row.empresa_cnpj_id ?? null,
  papelCrm: (row.papel_crm ?? null) as ClienteHub['papelCrm'],
  matrizNome: row.matriz_nome ?? null,
  implantacaoLiberada: row.implantacao_liberada === true,
  contratoStatus: (row.contrato_status ?? null) as ContratoStatus | null,
  dataVenda: row.data_venda ?? null,
  vidasContratadas: Number(row.vidas_contratadas || 0),
  valorMensalCents: Number(row.valor_mensal_cents || 0),
  vidasCadastradas: Number(row.vidas_cadastradas || 0),
  ultimaImportacaoStatus: (row.ultima_importacao_status ?? null) as ImportacaoStatus | null,
  ultimaImportacaoEm: row.ultima_importacao_em ?? null,
  sugestoes: Array.isArray(row.sugestoes) ? row.sugestoes.map(mapSugestaoVinculoHub) : [],
});
/* eslint-enable @typescript-eslint/no-explicit-any */

const contratoCampos = (dados: ContratoDados) => ({
  razao_social: dados.razaoSocial || null,
  cnpj: dados.cnpj || null,
  endereco: dados.endereco || null,
  email: dados.email || null,
  representante_nome: dados.representanteNome || null,
  representante_cpf: dados.representanteCpf || null,
  email_assinatura: dados.emailAssinatura || null,
  representante_qualificacao: qualificacaoOuPadrao(dados.representanteQualificacao),
  representante_declara_poderes: dados.representanteDeclaraPoderes === true,
});

// Sem `dados` os campos ficam FORA do payload, e não nulos: no UPDATE,
// nulo apagaria o que a aba Proposta ou o formulário do cliente gravou.
const contratoPayload = (input: UpsertContratoInput, dados: ContratoDados | null) => ({
  workspace_id: input.workspaceId,
  empresa_id: input.empresaId,
  sale_id: input.saleId || null,
  lead_id: input.leadId || null,
  ...(dados ? contratoCampos(dados) : {}),
  modalidade: input.modalidade,
  vida_mais_cobranca: input.vidaMaisCobranca,
  vidas_contratadas: input.vidasContratadas,
  valor_mensal_cents: input.valorMensalCents,
  data_venda: input.dataVenda || null,
});

const uuid = (): string => {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Fallback para contexto não-seguro: o nome do arquivo só precisa não
  // colidir dentro da pasta da empresa.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// Lotes de INSERT. 500 linhas é o que cabe confortavelmente numa
// requisição PostgREST sem estourar o limite de corpo do Nginx.
const CHUNK = 500;

export class SupabaseCsRepository implements ICsRepository {
  // ── Contrato ──────────────────────────────────────────────────

  getContratoById = async (contratoId: string): Promise<Contrato | null> => {
    const { data, error } = await cs()
      .from('contratos').select(CONTRATO_COLUMNS)
      .eq('id', contratoId).maybeSingle();
    if (error) throw error;
    return data ? mapContrato(data) : null;
  };

  getContratoBySale = async (saleId: string): Promise<Contrato | null> => {
    const { data, error } = await cs()
      .from('contratos').select(CONTRATO_COLUMNS)
      .eq('sale_id', saleId).maybeSingle();
    if (error) throw error;
    return data ? mapContrato(data) : null;
  };

  getContratoByEmpresa = async (empresaId: string): Promise<Contrato | null> => {
    const { data, error } = await cs()
      .from('contratos').select(CONTRATO_COLUMNS)
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false })
      .limit(1).maybeSingle();
    if (error) throw error;
    return data ? mapContrato(data) : null;
  };

  /**
   * Upsert feito com SELECT + INSERT/UPDATE, e não com
   * `.upsert({ onConflict: 'sale_id' })`.
   *
   * O motivo é o índice: `ux_cs_contratos_sale` é PARCIAL
   * (`WHERE sale_id IS NOT NULL`), e `ON CONFLICT (sale_id)` só casa com
   * índice parcial se o statement repetir o mesmo predicado — coisa que o
   * PostgREST não tem como expressar. Tentar assim volta 42P10.
   */
  upsertContrato = async (input: UpsertContratoInput): Promise<Contrato> => {
    const dados = input.dados;
    const payload = contratoPayload(input, dados);

    // A busca era SÓ por `sale_id`. Desde que o contrato pode nascer da
    // PROPOSTA — antes de existir venda — isso inseria uma linha nova a cada
    // save, e mais uma quando a venda chegasse: o link que o cliente já tinha
    // preenchido ficava apontando para um contrato órfão, porque
    // `getContratoByEmpresa` passa a devolver o mais recente.
    let existente = input.saleId ? await this.getContratoBySale(input.saleId) : null;
    if (!existente) {
      const daEmpresa = await this.getContratoByEmpresa(input.empresaId);
      // Adota só o contrato que ainda não é de nenhuma venda. O de OUTRA
      // venda da mesma empresa pertence a ela — sequestrar seria mover um
      // documento já em circulação de uma venda para outra.
      // Cancelado também não: a empresa que o Super Admin ocultou e volta a
      // negociar ganha contrato novo, em vez de herdar um que a carteira esconde.
      if (daEmpresa && !daEmpresa.saleId && daEmpresa.status !== 'cancelado') existente = daEmpresa;
    }

    let salvo: Contrato;
    if (existente) {
      // O status só sobe: contrato já enviado/assinado não volta a
      // `pendente` porque alguém reabriu a venda e limpou um campo.
      // Sem `dados` os campos não mudam, então o status que deriva deles também não.
      const status: ContratoStatus = dados && (existente.status === 'pendente' || existente.status === 'dados_completos')
        ? contratoStatusInicial(dados, input.modalidade)
        : existente.status;

      // Vínculo não é apagado por quem não o conhece. A aba Proposta salva
      // sem `saleId`; com o payload cru, o UPDATE zeraria o `sale_id` que o
      // fechamento da venda tinha acabado de gravar.
      const { sale_id, lead_id, data_venda, ...resto } = payload;
      const vinculos = {
        ...(sale_id ? { sale_id } : {}),
        ...(lead_id ? { lead_id } : {}),
        ...(data_venda ? { data_venda } : {}),
      };

      const { data, error } = await cs()
        .from('contratos').update({ ...resto, ...vinculos, status })
        .eq('id', existente.id).select(CONTRATO_COLUMNS).single();
      if (error) throw error;
      salvo = mapContrato(data);
    } else {
      const { data, error } = await cs()
        .from('contratos').insert({ ...payload, status: contratoStatusInicial(dados || emptyContratoDados(), input.modalidade) })
        .select(CONTRATO_COLUMNS).single();
      if (error) throw error;
      salvo = mapContrato(data);
    }

    await this.sincronizarDocumentos(salvo.id, input.documentos);
    return salvo;
  };

  /**
   * Cria os documentos que faltam. NÃO apaga os que sobram: um adicional
   * removido da proposta depois do contrato ter sido enviado ao D4Sign
   * deixaria um documento em circulação sem registro nosso. Cancelar é
   * ação explícita do CS, não efeito colateral de editar a venda.
   */
  private sincronizarDocumentos = async (
    contratoId: string,
    tipos: DocumentoTipo[],
  ): Promise<void> => {
    if (!tipos.length) return;
    const { error } = await cs()
      .from('contrato_documentos')
      .upsert(
        tipos.map(tipo => ({ contrato_id: contratoId, tipo })),
        { onConflict: 'contrato_id,tipo', ignoreDuplicates: true },
      );
    if (error) throw error;
  };

  listDocumentos = async (contratoId: string): Promise<ContratoDocumento[]> => {
    const { data, error } = await cs()
      .from('contrato_documentos')
      .select('id, contrato_id, tipo, provider, provider_doc_id, status, url, signed_at')
      .eq('contrato_id', contratoId)
      .order('tipo', { ascending: true });
    if (error) throw error;
    return (data || []).map(mapDocumento);
  };

  setDocumentoStatus = async (documentoId: string, status: DocumentoStatus): Promise<ContratoDocumento> => {
    // `signed_at` acompanha o status: sair de assinado sem limpar a data
    // deixaria um documento pendente com carimbo de assinatura.
    const { data, error } = await cs()
      .from('contrato_documentos')
      .update({ status, signed_at: status === 'assinado' ? new Date().toISOString() : null })
      .eq('id', documentoId)
      .select('id, contrato_id, tipo, provider, provider_doc_id, status, url, signed_at')
      .single();
    if (error) throw error;
    return mapDocumento(data);
  };

  listCompetencias = async (contratoId: string): Promise<Competencia[]> => {
    const colunas = 'id, contrato_id, competencia, vidas, valor_cents, vencimento, status, boleto_url, pago_em, pago_manual';
    const ler = (extra: string) => cs().from('competencias')
      .select(colunas + extra).eq('contrato_id', contratoId).order('competencia', { ascending: false });
    let { data, error } = await ler(', boleto_linha_digitavel');
    // Sem a migration 20261016120000 a coluna não existe: lê sem ela.
    if (error?.code === '42703') ({ data, error } = await ler(''));
    if (error) throw error;
    return (data || []).map(mapCompetencia);
  };

  criarCompetenciaAtual = async (contratoId: string): Promise<Competencia> => {
    const { data, error } = await cs().rpc('criar_competencia_atual', { p_contrato_id: contratoId });
    if (error) throw error;
    return mapCompetencia(data);
  };

  previaBoleto = async (contratoId: string): Promise<PreviaBoleto> => {
    const { data, error } = await cs().rpc('previa_boleto', { p_contrato_id: contratoId });
    if (error) throw error;
    const row = (Array.isArray(data) ? data[0] : data) || {};
    return {
      vidas: Number(row.vidas || 0),
      valorCents: Number(row.valor_cents || 0),
      vidasContratadas: Number(row.vidas_contratadas || 0),
      valorContratoCents: Number(row.valor_contrato_cents || 0),
      rascunhosPendentes: Number(row.rascunhos_pendentes || 0),
    };
  };

  marcarCompetenciaPaga = async (competenciaId: string, paga: boolean): Promise<Competencia> => {
    const { data, error } = await cs().rpc('marcar_competencia_paga', {
      p_competencia_id: competenciaId, p_paga: paga,
    });
    if (error) throw error;
    return mapCompetencia(data);
  };

  listPlanosContratados = async (saleId: string | null): Promise<PlanoContratado[]> => {
    if (!saleId) return [];
    const { data, error } = await client().from('sale_plans')
      .select('name_snapshot, quantity, unit_value_snapshot, total_value, kind_snapshot, catalog_value_snapshot, price_months')
      .eq('sale_id', saleId).order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []).map(row => ({
      nome: row.name_snapshot,
      quantidade: Number(row.quantity || 0),
      valorUnitario: Number(row.unit_value_snapshot || 0),
      valorTotal: Number(row.total_value || 0),
      adicional: row.kind_snapshot === 'addon',
      valorTabela: row.catalog_value_snapshot == null ? null : Number(row.catalog_value_snapshot),
      meses: row.price_months == null ? null : Number(row.price_months),
    }));
  };

  // O preparo é um `boleto.gerar` cujo payload diz `cs.boleto.preparar`. Vale
  // o MAIS RECENTE, de qualquer status — mesma régua do servidor
  // (`preparoDa`), que é quem trava a geração de verdade. Em voo
  // (pendente/enviando) conta como falha: a tela não oferece reenvio cego.
  ultimoPreparoBoleto = async (competenciaId: string): Promise<{ preparo: PreparoBoleto | null; ultimoFalhou: boolean }> => {
    const { data, error } = await cs().from('gatilhos')
      .select('status, payload, response, sent_at, created_at')
      .eq('competencia_id', competenciaId).eq('tipo', 'boleto.gerar')
      .eq('payload->>event', 'cs.boleto.preparar')
      .order('created_at', { ascending: false }).limit(1);
    if (error) throw error;
    const row = (data || [])[0];
    if (!row) return { preparo: null, ultimoFalhou: false };
    if (row.status !== 'sucesso') return { preparo: null, ultimoFalhou: true };
    const conferencia = row.payload?.cobranca?.conferencia;
    const hub = hubDaResposta(row.response);
    if (!conferencia || !hub) return { preparo: null, ultimoFalhou: true };
    return { preparo: { enviadoEm: row.sent_at || row.created_at, conferencia, hub }, ultimoFalhou: false };
  };

  // ── Vidas ─────────────────────────────────────────────────────

  listVidas = async (empresaId: string): Promise<Vida[]> => {
    const { data, error } = await cs()
      .from('vidas').select(VIDA_COLUMNS)
      .eq('empresa_id', empresaId).eq('ativo', true)
      .order('nome', { ascending: true });
    if (error) throw error;
    return (data || []).map(mapVida);
  };

  criarVida = async (input: CriarVidaInput): Promise<Vida> => {
    const { data, error } = await cs()
      .from('vidas')
      .insert({
        workspace_id: input.workspaceId,
        empresa_id: input.empresaId,
        contrato_id: input.contratoId || null,
        tipo: input.tipo || 'colaborador',
        titular_id: input.titularId || null,
        nome: input.nome,
        cpf: input.cpf,
        data_nascimento: input.dataNascimento,
        email: input.email,
        telefone: input.telefone || null,
        plano_id: input.planoId || null,
        plano_nome_raw: input.planoNomeRaw,
        odonto: input.odonto === true,
        psicologo: input.psicologo === true,
        nome_mae: input.nomeMae || null,
      })
      .select(VIDA_COLUMNS)
      .single();
    if (error) throw error;
    return mapVida(data);
  };

  atualizarVida = async (vidaId: string, patch: AtualizarVidaInput): Promise<Vida> => {
    // Só o que veio no patch vai para o banco: mandar `undefined` como null
    // apagaria telefone e plano de quem so mudou o nome.
    const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (patch.nome !== undefined) row.nome = patch.nome;
    if (patch.cpf !== undefined) row.cpf = patch.cpf;
    if (patch.dataNascimento !== undefined) row.data_nascimento = patch.dataNascimento;
    if (patch.email !== undefined) row.email = patch.email;
    if (patch.telefone !== undefined) row.telefone = patch.telefone || null;
    if (patch.planoId !== undefined) row.plano_id = patch.planoId || null;
    if (patch.planoNomeRaw !== undefined) row.plano_nome_raw = patch.planoNomeRaw;
    if (patch.odonto !== undefined) row.odonto = patch.odonto === true;
    if (patch.psicologo !== undefined) row.psicologo = patch.psicologo === true;
    if (patch.nomeMae !== undefined) row.nome_mae = patch.nomeMae || null;
    if (patch.tipo !== undefined) row.tipo = patch.tipo;
    if (patch.titularId !== undefined) row.titular_id = patch.titularId || null;
    if (patch.status !== undefined) row.status = patch.status;
    if (patch.ativo !== undefined) row.ativo = patch.ativo;

    const { data, error } = await cs()
      .from('vidas').update(row).eq('id', vidaId).select(VIDA_COLUMNS).single();
    if (error) throw error;
    return mapVida(data);
  };

  registrarGatilho = async (input: RegistrarGatilhoInput): Promise<void> => {
    const { error } = await cs().from('gatilhos').insert({
      workspace_id: input.workspaceId,
      empresa_id: input.empresaId,
      contrato_id: input.contratoId || null,
      competencia_id: input.competenciaId || null,
      vida_id: input.vidaId || null,
      importacao_id: input.importacaoId || null,
      tipo: input.tipo,
      payload: input.payload,
      transporte: input.transporte,
      request_id: input.requestId,
      dedupe_key: input.dedupeKey,
      status: input.status,
      tentativas: 1,
      http_status: input.httpStatus ?? null,
      response: input.response ?? null,
      ultimo_erro: input.ultimoErro || null,
      sent_at: new Date().toISOString(),
    });
    if (error) throw error;
  };

  gatilhoJaTeveSucesso = async (empresaId: string, tipo: GatilhoTipo, dedupeKey: string): Promise<boolean> => {
    const { data, error } = await cs()
      .from('gatilhos').select('id')
      .eq('empresa_id', empresaId).eq('tipo', tipo)
      .eq('dedupe_key', dedupeKey).eq('status', 'sucesso')
      .limit(1);
    if (error) throw error;
    return (data || []).length > 0;
  };

  removerVida = async (vidaId: string): Promise<void> => {
    const { error } = await cs().from('vidas').delete().eq('id', vidaId);
    if (error) throw error;
  };

  getIdentificadoresDaEmpresa = async (empresaId: string): Promise<IdentificadoresDaEmpresa> => {
    const { data, error } = await cs()
      .from('vidas').select('cpf, email, tipo')
      .eq('empresa_id', empresaId).eq('ativo', true);
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const linhas = (data || []) as any[];
    return {
      // CPF é único pra todo mundo, inclusive só adicional.
      cpfs: linhas.map(r => String(r.cpf || '')).filter(Boolean),
      // E-mail de só adicional fica fora: ele pode repetir o de qualquer
      // pessoa da empresa (o caso real é o dependente com o e-mail da mãe
      // titular), então não pode ocupar a vaga no índice de duplicidade.
      emails: linhas
        .filter(r => r.tipo !== 'so_adicional')
        .map(r => String(r.email || '')).filter(Boolean),
      cpfsColaboradores: linhas
        .filter(r => !ehDependente((r.tipo || 'colaborador') as VidaTipo))
        .map(r => String(r.cpf || '')).filter(Boolean),
    };
  };

  // ── Importação ────────────────────────────────────────────────

  private uploadPlanilha = async (
    workspaceId: string,
    empresaId: string,
    arquivo: ArquivoPlanilha,
  ): Promise<string> => {
    // Convenção casada com as policies do bucket: a primeira pasta é o
    // workspace, e é por ela que a RLS do storage decide (cs.path_workspace_id).
    const path = `${workspaceId}/${empresaId}/${uuid()}.${arquivo.formato}`;
    const { error } = await client().storage
      .from(CS_BUCKET)
      .upload(path, arquivo.blob, { upsert: false, contentType: arquivo.blob.type || undefined });
    if (error) throw error;
    return path;
  };

  criarImportacao = async (input: CriarImportacaoInput): Promise<Importacao> => {
    // Sobe o arquivo ANTES de criar a linha: falha de upload não pode
    // deixar uma importação órfã apontando para nada.
    const arquivoPath = input.arquivo
      ? await this.uploadPlanilha(input.workspaceId, input.empresaId, input.arquivo)
      : null;

    const { data: criada, error: errImp } = await cs()
      .from('vidas_importacoes')
      .insert({
        workspace_id: input.workspaceId,
        empresa_id: input.empresaId,
        contrato_id: input.contratoId || null,
        sale_id: input.saleId || null,
        arquivo_path: arquivoPath,
        arquivo_nome: input.arquivo?.nome || null,
        arquivo_hash: input.arquivo?.hash || null,
        formato: input.arquivo?.formato || null,
        total_linhas: input.totalLinhas,
        total_validas: input.totalValidas,
        total_erros: input.totalErros,
        erros: input.erros,
        negociacao: input.negociacao || null,
        total_descartadas: input.totalDescartadas || 0,
        // Começa em 0: se a gravação abortar no meio, o histórico não pode
        // mostrar rascunhos que não existem. O update final grava o real.
        total_rascunhos: 0,
        status: 'processando',
      })
      .select(IMPORTACAO_COLUMNS).single();
    if (errImp) throw errImp;

    const importacao = mapImportacao(criada);

    // Insere as vidas em lotes. Um lote que falhar não derruba os
    // anteriores: o erro entra no registro da importação e o comercial vê
    // exatamente o que não entrou, em vez de perder as 400 que entraram.
    const errosDeInsercao: PlanilhaErro[] = [];
    let inseridas = 0;

    const linhaDeVida = (v: VidaImportada, titularId: string | null) => ({
      workspace_id: input.workspaceId,
      empresa_id: input.empresaId,
      contrato_id: input.contratoId || null,
      importacao_id: importacao.id,
      tipo: v.tipo,
      titular_id: titularId,
      nome: v.nome,
      cpf: v.cpf,
      data_nascimento: v.dataNascimento,
      email: v.email,
      telefone: v.telefone || null,
      plano_id: v.planoId,
      plano_nome_raw: v.planoNomeRaw,
      odonto: v.odonto,
      psicologo: v.psicologo,
      nome_mae: v.nomeMae || null,
      status: 'pendente',
    });

    const gravarEmLotes = async (
      lista: VidaImportada[],
      titularDe: (v: VidaImportada) => string | null,
    ) => {
      for (let i = 0; i < lista.length; i += CHUNK) {
        const lote = lista.slice(i, i + CHUNK);
        const { error } = await cs().from('vidas')
          .insert(lote.map(v => linhaDeVida(v, titularDe(v))));

        if (error) {
          errosDeInsercao.push({
            linha: lote[0].linha,
            aba: lote[0].aba ?? (ehDependente(lote[0].tipo) ? 'Vida+' : 'Colaborador'),
            motivo: `linhas ${lote[0].linha}–${lote[lote.length - 1].linha} não gravadas: ${error.message}`,
          });
        } else {
          inseridas += lote.length;
        }
      }
    };

    // Duas fases, e a ordem não é escolha: o dependente (Vida+ ou só
    // adicional) aponta para o id do titular, que só existe depois que o
    // colaborador entrou. `validarPlanilha` já devolve a lista nessa ordem;
    // aqui separamos de novo porque entre as duas fases é preciso reler os
    // ids que o banco gerou.
    const colaboradores = input.vidas.filter(v => !ehDependente(v.tipo));
    const dependentes = input.vidas.filter(v => ehDependente(v.tipo));

    await gravarEmLotes(colaboradores, () => null);

    if (dependentes.length) {
      // Relê TODOS os colaboradores ativos da empresa, não só os deste lote:
      // um Vida+ pode se pendurar em quem entrou numa importação anterior.
      const { data: titulares, error: errTit } = await cs()
        .from('vidas').select('id, cpf')
        .eq('empresa_id', input.empresaId).eq('ativo', true).eq('tipo', 'colaborador');
      if (errTit) throw errTit;

      const porCpf = new Map<string, string>();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((titulares || []) as any[]).forEach((t) => {
        if (t.cpf && !porCpf.has(String(t.cpf))) porCpf.set(String(t.cpf), t.id);
      });

      // Órfão vira erro de linha, não exceção: as demais vidas do arquivo
      // continuam entrando. Só acontece quando o titular falhou ao gravar,
      // porque a validação já barrou o vínculo inexistente.
      const orfaos = dependentes.filter(v => !porCpf.get(String(v.titularCpf || '')));
      orfaos.forEach(v => errosDeInsercao.push({
        linha: v.linha, aba: 'Vida+',
        motivo: `${v.nome}: o colaborador vinculado não foi gravado, então a vida ficou de fora.`,
      }));

      await gravarEmLotes(
        dependentes.filter(v => porCpf.get(String(v.titularCpf || ''))),
        v => porCpf.get(String(v.titularCpf || '')) || null,
      );
    }

    // Rascunhos por último e com o id da importação: são o que não virou vida
    // (D1 ou escolha na prévia). Lote que falhar vira erro registrado, como as
    // vidas — e `total_rascunhos` fica com o que de fato entrou.
    // Reenviar o mesmo arquivo não duplica: quem já tem rascunho pendente
    // nesta empresa (mesmo CPF) fica de fora, com o motivo registrado.
    let rascunhos: LinhaParaRascunho[] = input.rascunhos || [];
    if (rascunhos.length) {
      const { data: pendentes, error: errPend } = await cs()
        .from('vidas_rascunhos').select('linha')
        .eq('empresa_id', input.empresaId).eq('status', 'pendente');
      if (errPend) throw errPend;
      const cpfsPendentes = new Set(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((pendentes || []) as any[]).map(p => onlyDigits(String(p.linha?.cpf ?? ''))).filter(Boolean),
      );
      rascunhos = rascunhos.filter((r) => {
        const cpf = onlyDigits(String(r.linha.cpf ?? ''));
        // Só contra os pendentes de antes: duas linhas iguais NESTA planilha
        // vão juntas para rascunho, e o usuário decide qual fica.
        if (!cpf || !cpfsPendentes.has(cpf)) return true;
        errosDeInsercao.push({
          linha: r.linha.numero, aba: r.linha.aba,
          motivo: `${r.linha.nome || 'Linha'}: já existe rascunho pendente desta pessoa; não foi duplicado.`,
        });
        return false;
      });
    }
    let rascunhosGravados = 0;
    for (let i = 0; i < rascunhos.length; i += CHUNK) {
      const lote = rascunhos.slice(i, i + CHUNK);
      const { error } = await cs().from('vidas_rascunhos').insert(lote.map(r => ({
        workspace_id: input.workspaceId,
        empresa_id: input.empresaId,
        importacao_id: importacao.id,
        linha: r.linha,
        erros: r.erros || [],
        status: 'pendente',
      })));
      if (error) {
        errosDeInsercao.push({
          linha: lote[0].linha.numero,
          aba: lote[0].linha.aba,
          motivo: `rascunhos de ${lote.length} linha(s) não gravados: ${error.message}`,
        });
      } else {
        rascunhosGravados += lote.length;
      }
    }

    const erros = [...input.erros, ...errosDeInsercao];
    const status: ImportacaoStatus = erros.length ? 'com_erros' : 'validada';

    const { data: atualizada, error: errUp } = await cs()
      .from('vidas_importacoes')
      .update({ status, total_validas: inseridas, total_erros: erros.length, erros, total_rascunhos: rascunhosGravados })
      .eq('id', importacao.id).select(IMPORTACAO_COLUMNS).single();
    if (errUp) throw errUp;

    return mapImportacao(atualizada);
  };

  listImportacoes = async (empresaId: string): Promise<Importacao[]> => {
    const { data, error } = await cs()
      .from('vidas_importacoes').select(IMPORTACAO_COLUMNS)
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(mapImportacao);
  };

  listHistoricoImportacoes = async (empresaId: string): Promise<ImportacaoHistorico[]> => {
    const { data, error } = await cs().rpc('listar_importacoes_empresa', { p_empresa_id: empresaId });
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data || []) as any[]).map(mapImportacaoHistorico);
  };

  // ── Rascunhos de vidas ────────────────────────────────────────
  // Lista e edição parcial pela tabela (a RLS só deixa UPDATE em pendente);
  // resolver e descartar por RPC, que trava a linha e confere o status.

  listRascunhos = async (empresaId: string): Promise<VidaRascunho[]> => {
    const { data, error } = await cs()
      .from('vidas_rascunhos').select(RASCUNHO_COLUMNS)
      .eq('empresa_id', empresaId).eq('status', 'pendente')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(mapRascunho);
  };

  salvarRascunho = async (id: string, linha: PlanilhaLinha, erros: PlanilhaErro[]): Promise<VidaRascunho> => {
    const { data, error } = await cs()
      .from('vidas_rascunhos')
      .update({ linha, erros })
      .eq('id', id).eq('status', 'pendente')
      .select(RASCUNHO_COLUMNS).maybeSingle();
    if (error) throw error;
    // Zero linhas = deixou de ser pendente no meio-tempo (ou a RLS recusou).
    if (!data) throw new Error('Rascunho já resolvido ou descartado.');
    return mapRascunho(data);
  };

  resolverRascunho = async (id: string, vida: CriarVidaInput): Promise<Vida> => {
    const { data, error } = await cs().rpc('resolver_rascunho_vida', {
      p_rascunho_id: id,
      p_vida: {
        contrato_id: vida.contratoId || null,
        tipo: vida.tipo || 'colaborador',
        titular_id: vida.titularId || null,
        nome: vida.nome,
        cpf: vida.cpf,
        data_nascimento: vida.dataNascimento,
        email: vida.email,
        telefone: vida.telefone || null,
        plano_id: vida.planoId || null,
        plano_nome_raw: vida.planoNomeRaw,
        odonto: vida.odonto === true,
        psicologo: vida.psicologo === true,
        nome_mae: vida.nomeMae || null,
      },
    });
    if (error) throw error;
    return mapVida(Array.isArray(data) ? data[0] : data);
  };

  descartarRascunho = async (id: string): Promise<VidaRascunho> => {
    const { data, error } = await cs().rpc('descartar_rascunho_vida', { p_rascunho_id: id });
    if (error) throw error;
    return mapRascunho(Array.isArray(data) ? data[0] : data);
  };

  // ── Área de Implantação ───────────────────────────────────────
  // Três RPCs, nenhuma tabela: a autorização do grupo e o filtro de
  // `implantacao_liberada` vivem dentro delas
  // (20260922150000_clientes_implantacao.sql). Consultar as tabelas daqui
  // passaria pela RLS do CS, que responde a outra pergunta.

  currentUserIsImplantacao = async (): Promise<boolean> => {
    const { data, error } = await cs().rpc('current_user_is_implantacao');
    if (error) throw error;
    return data === true;
  };

  listClientesImplantacao = async (): Promise<ClienteImplantacao[]> => {
    const { data, error } = await cs().rpc('listar_clientes_implantacao');
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data || []) as any[]).map(mapClienteImplantacao);
  };

  getClienteImplantacao = async (empresaId: string): Promise<ClienteImplantacaoResultado> => {
    const { data, error } = await cs().rpc('obter_cliente_implantacao', {
      p_empresa_id: empresaId,
    });
    if (error) throw error;

    // A RPC devolve 0 linhas para empresa inexistente e 1 linha com
    // `liberada = false` (resto nulo) para a que existe mas não foi
    // liberada — são estados diferentes na tela.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const row = ((data || []) as any[])[0];
    if (!row) return { estado: 'inexistente' };
    if (row.liberada !== true) return { estado: 'nao_liberada' };

    return {
      estado: 'ok',
      cliente: {
        ...mapClienteImplantacao(row),
        endereco: row.endereco ?? null,
        razaoSocial: row.razao_social ?? null,
        email: row.contrato_email ?? null,
        representanteNome: row.representante_nome ?? null,
        emailAssinatura: row.email_assinatura ?? null,
        vidaMaisCobranca: (row.vida_mais_cobranca ?? null) as VidaMaisCobranca | null,
        diaVencimento: row.dia_vencimento == null ? null : Number(row.dia_vencimento),
        ultimaImportacaoArquivo: row.ultima_importacao_arquivo ?? null,
        ultimaImportacaoValidas:
          row.ultima_importacao_validas == null ? null : Number(row.ultima_importacao_validas),
        ultimaImportacaoErros:
          row.ultima_importacao_erros == null ? null : Number(row.ultima_importacao_erros),
        negociacao: (row.negociacao ?? null) as NegociacaoPlanilha | null,
      },
    };
  };

  // ── Ficha do cliente (cs.obter_ficha_cliente) ───────────────────
  // Uma RPC só, dois públicos: `estadoDaFicha` (csModel, pura) decide entre
  // completo/reduzido/nao_encontrado/sem_acesso a partir da linha + do
  // código do erro. Só o `42501` vira estado — qualquer outro erro sobe
  // (o React Query é quem expõe, via `.error`, como já faz o resto do CS).

  obterFichaCliente = async (empresaId: string): Promise<EstadoFicha> => {
    const { data, error } = await cs().rpc('obter_ficha_cliente', { p_empresa_id: empresaId });
    if (error && error.code !== '42501') throw error;
    return estadoDaFicha((data || []) as FichaClienteRow[], error?.code ?? null);
  };

  // ── Automações (aba da ficha completa) ──────────────────────────

  // Embute documentos e competências (FK no mesmo schema) para não fazer
  // uma consulta por empresa. O nome vem de public.empresas à parte — sem
  // join entre schemas no PostgREST; o que a RLS esconder cai na razão social.
  listContratosDaCarteira = async (): Promise<ContratoDaCarteira[]> => {
    const { data, error } = await cs().from('contratos')
      .select('id, workspace_id, empresa_id, razao_social, cnpj, modalidade, created_at, contrato_documentos(status), competencias(competencia, status)')
      .neq('status', 'cancelado');
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows = (data || []) as any[];
    const ids = Array.from(new Set(rows.map(r => r.empresa_id as string)));
    const nomes = new Map<string, string>();
    if (ids.length) {
      const { data: empresas, error: erroEmpresas } = await client().from('empresas').select('id, nome').in('id', ids);
      if (erroEmpresas) throw erroEmpresas;
      for (const e of empresas || []) nomes.set(e.id, e.nome || '');
    }
    return rows.map(r => ({
      contratoId: r.id,
      empresaId: r.empresa_id,
      workspaceId: r.workspace_id,
      empresaNome: nomes.get(r.empresa_id) || '',
      razaoSocial: r.razao_social ?? '',
      cnpj: r.cnpj ?? '',
      modalidade: (r.modalidade || 'desconto_folha') as ContratoModalidade,
      createdAt: r.created_at,
      documentos: (r.contrato_documentos || []).map((d: { status: string }) => ({ status: (d.status || 'pendente') as DocumentoStatus })),
      competencias: (r.competencias || []).map((c: { competencia: string; status: string }) => ({
        competencia: c.competencia, status: c.status as CompetenciaStatus,
      })),
    }));
  };

  ocultarEmpresaDaCarteira = async (empresaId: string): Promise<number> => {
    const { data, error } = await cs().rpc('admin_ocultar_empresa_da_carteira', { p_empresa_id: empresaId });
    if (error) throw error;
    return Number(data) || 0;
  };

  listGatilhosEmpresa = async (empresaId: string): Promise<GatilhoEmpresa[]> => {
    const { data, error } = await cs().rpc('listar_gatilhos_empresa', { p_empresa_id: empresaId });
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data || []) as any[]).map(row => ({
      id: row.id,
      tipo: row.tipo as GatilhoTipo,
      status: row.status as GatilhoStatus,
      transporte: (row.transporte ?? null) as GatilhoTransporte | null,
      tentativas: Number(row.tentativas) || 0,
      httpStatus: row.http_status == null ? null : Number(row.http_status),
      response: row.response ?? null,
      ultimoErro: row.ultimo_erro ?? null,
      repetirCom: row.repetir_com ?? null,
      idempotencyKey: row.idempotency_key ?? null,
      createdAt: row.created_at,
      sentAt: row.sent_at ?? null,
    }));
  };

  // ── Rota de integração ──────────────────────────────────────────

  getRotaIntegracao = async (empresaId: string): Promise<RotaIntegracao> => {
    const { data, error } = await cs().rpc('rota_integracao', { p_empresa_id: empresaId });
    // Sem a migration 20260930120000 não há rota gravada: é o n8n de sempre,
    // o mesmo desfecho que o servidor adota.
    if (error && (error.code === 'PGRST202' || error.code === '42883')) return ROTA_PADRAO;
    if (error) throw error;
    const row = (Array.isArray(data) ? data[0] : data) as { contrato_via?: RotaVia; cobranca_via?: RotaVia } | null;
    return {
      contratoVia: row?.contrato_via === 'hub' ? 'hub' : 'n8n',
      cobrancaVia: row?.cobranca_via === 'hub' ? 'hub' : 'n8n',
    };
  };

  getRotaGlobal = async (): Promise<RotaVia> => {
    const { data, error } = await cs().from('rota_integracao_global').select('via').maybeSingle();
    if (error) throw error;
    return data?.via === 'hub' ? 'hub' : 'n8n';
  };

  /** A linha é única e nasce na migration; aqui só se atualiza. Zero linhas
   *  afetadas = a RLS recusou (não é owner) ou a migration não foi aplicada. */
  salvarRotaGlobal = async (via: RotaVia): Promise<void> => {
    const { data, error } = await cs().from('rota_integracao_global')
      .update({ via }).eq('id', true).select('via');
    if (error) throw error;
    if (!data?.length) throw new Error('A rota não foi salva: só o owner da plataforma pode mudar.');
  };

  buscarEmpresasParaRota = async (workspaceId: string, query: string): Promise<EmpresaParaRota[]> => {
    const { data, error } = await cs().rpc('admin_buscar_empresas', { p_workspace_id: workspaceId, p_query: query });
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data || []) as any[]).map(row => ({ id: row.id, nome: row.nome ?? '', cnpj: row.cnpj ?? null }));
  };

  // ── Envios desativados ──────────────────────────────────────────

  listEnviosBloqueados = async (empresaId: string): Promise<EnvioBloqueado[]> => {
    const { data, error } = await cs()
      .from('envios_bloqueados').select('tipo, motivo').eq('empresa_id', empresaId);
    // Sem a migration 20260930140000 nada está desativado — o mesmo desfecho
    // que o servidor adota.
    if (error && (error.code === 'PGRST205' || error.code === '42P01')) return [];
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data || []) as any[]).map(row => ({ tipo: row.tipo as GatilhoTipo, motivo: row.motivo ?? '' }));
  };

  listEnviosBloqueadosAdmin = async (): Promise<EnvioBloqueadoRegistro[]> => {
    const { data, error } = await cs().rpc('admin_listar_envios_bloqueados');
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data || []) as any[]).map(row => ({
      id: row.id,
      workspaceId: row.workspace_id,
      workspaceNome: row.workspace_nome ?? '',
      empresaId: row.empresa_id,
      empresaNome: row.empresa_nome ?? '',
      empresaCnpj: row.empresa_cnpj ?? null,
      tipo: row.tipo as GatilhoTipo,
      motivo: row.motivo ?? '',
      createdAt: row.created_at,
    }));
  };

  bloquearEnvio = async (workspaceId: string, empresaId: string, tipo: GatilhoTipo, motivo: string): Promise<void> => {
    // O trigger reescreve o workspace a partir da empresa, como em rotas_integracao.
    const { error } = await cs().from('envios_bloqueados')
      .insert({ workspace_id: workspaceId, empresa_id: empresaId, tipo, motivo });
    if (error?.code === '23505') throw new Error('Este envio já está desativado para esta empresa.');
    if (error) throw error;
  };

  desbloquearEnvio = async (id: string): Promise<void> => {
    const { error } = await cs().from('envios_bloqueados').delete().eq('id', id);
    if (error) throw error;
  };

  // ── Clientes do Hub (/clientes, aba Hub) ────────────────────────
  // Quatro RPCs, nenhuma tabela consultada direto: a autorização
  // (`pode_ver_clientes_hub`), o vínculo sugerido por CNPJ e a confirmação
  // dele vivem todas no banco — aqui é só a chamada e o mapeamento.

  podeVerClientesHub = async (): Promise<boolean> => {
    const { data, error } = await cs().rpc('pode_ver_clientes_hub');
    if (error) throw error;
    return data === true;
  };

  podeVincularEmpresaHub = async (): Promise<boolean> => {
    const { data, error } = await cs().rpc('current_user_pode_vincular_hub');
    if (error) throw error;
    return data === true;
  };

  listClientesHub = async (): Promise<ClienteHub[]> => {
    const { data, error } = await cs().rpc('listar_clientes_hub');
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data || []) as any[]).map(mapClienteHub);
  };

  ultimaSincronizacaoHub = async (): Promise<UltimaSincronizacaoHub | null> => {
    const { data, error } = await cs().rpc('ultima_sincronizacao_hub');
    if (error) throw error;
    // A RPC devolve um objeto só; fica robusto também a vir como linha única.
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;
    return {
      iniciadoEm: row.iniciado_em ?? null,
      terminadoEm: row.terminado_em ?? null,
      total: Number(row.total || 0),
      gravadas: Number(row.gravadas || 0),
      erro: row.erro ?? null,
    };
  };

  vincularEmpresaHub = async (hubPublicId: string, empresaId: string, como: 'matriz' | 'filial'): Promise<void> => {
    const { error } = await cs().rpc('vincular_empresa_hub', {
      p_hub_public_id: hubPublicId,
      p_empresa_id: empresaId,
      p_como: como,
    });
    if (error) throw error;
  };

  desvincularEmpresaHub = async (hubPublicId: string): Promise<void> => {
    const { error } = await cs().rpc('desvincular_empresa_hub', { p_hub_public_id: hubPublicId });
    if (error) throw error;
  };

  buscarEmpresasParaVinculo = async (termo: string, marca: string): Promise<EmpresaParaVinculo[]> => {
    const { data, error } = await cs().rpc('buscar_empresas_para_vinculo', { p_termo: termo, p_marca: marca });
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data || []) as any[]).map(mapEmpresaParaVinculo);
  };

  listContasHubDaEmpresa = async (empresaId: string): Promise<ContaHubDaEmpresa[]> => {
    const { data, error } = await cs().rpc('contas_hub_da_empresa', { p_empresa_id: empresaId });
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data || []) as any[]).map(mapContaHubDaEmpresa);
  };

  empresaTravadaPeloHub = async (empresaId: string): Promise<boolean> => {
    const { data, error } = await cs().rpc('empresa_travada_pelo_hub', { p_empresa_id: empresaId });
    if (error) throw error;
    return data === true;
  };
}
